import { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';

import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/lib/auth';
import { cn } from '@/lib/cn';

const NAV_LINKS = [
  { to: '/',         labelKey: 'nav.home'     },
  { to: '/about',    labelKey: 'nav.about'    },
  { to: '/services', labelKey: 'nav.services' },
  { to: '/why-us',   labelKey: 'nav.why_us'   },
  { to: '/order',    labelKey: 'nav.catalog'  },
  { to: '/contact',  labelKey: 'nav.contact'  },
] as const;

export function Header() {
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = useAuthStore((s) => s.user);

  function toggleLang() {
    const next = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(next);
  }

  return (
    <header className="sticky top-0 z-40 bg-bone/85 backdrop-blur-md border-b border-line">
      <div className="container flex items-center justify-between h-16 md:h-20">
        <Logo className="text-xl md:text-2xl" />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              className={({ isActive }) =>
                cn(
                  'px-4 py-2 text-sm font-body transition-colors duration-150',
                  isActive ? 'text-ink' : 'text-charcoal hover:text-ink',
                )
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {t(link.labelKey)}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-px bg-gold" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleLang}
            className="hidden sm:inline-flex items-center px-3 h-9 text-sm font-body text-charcoal hover:text-ink transition-colors border-l border-line/60"
            aria-label="Toggle language"
          >
            {t('nav.language')}
          </button>

          {user ? (
            <Link to="/account" className="hidden md:inline-block">
              <Button variant="ghost" size="sm">{t('nav.account')}</Button>
            </Link>
          ) : (
            <Link to="/login" className="hidden md:inline-block">
              <Button variant="ghost" size="sm">{t('nav.login')}</Button>
            </Link>
          )}

          <Link to="/order" className="hidden md:inline-block">
            <Button variant="primary" size="sm">{t('nav.book')}</Button>
          </Link>

          {/* Mobile menu trigger */}
          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-ink"
            aria-label="Menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu sheet */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-line bg-bone animate-fade-up">
          <nav className="container py-6 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'py-3 font-body text-base',
                    isActive ? 'text-ink' : 'text-charcoal',
                  )
                }
              >
                {t(link.labelKey)}
              </NavLink>
            ))}
            <div className="pt-4 mt-2 border-t border-line/60 flex flex-col gap-2">
              <button
                type="button"
                onClick={toggleLang}
                className="self-start py-2 text-sm font-body text-charcoal"
              >
                {t('nav.language')}
              </button>
              {user ? (
                <Link to="/account" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" size="md" className="w-full">{t('nav.account')}</Button>
                </Link>
              ) : (
                <Link to="/login" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" size="md" className="w-full">{t('nav.login')}</Button>
                </Link>
              )}
              <Link to="/order" onClick={() => setMobileOpen(false)}>
                <Button variant="primary" size="md" className="w-full">{t('nav.book')}</Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
